import type { AppContribution } from "@/composition";
import { createRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, on, Show, Suspense } from "solid-js";
import { Dynamic } from "solid-js/web";
import { ChevronsUpDown, Menu, UserCircle } from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import { rootRoute } from "../__root";
import { sessionActor } from "@/lib/session-machine";
import { accountsStore, readAccountsStateSync, readActiveAccountSync } from "@/lib/accounts";
import { ensureTabAccount } from "@/lib/active-account";
import { api, fetchApi } from "@/lib/api";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";
// F4-A: global undo (Ctrl/Cmd+Z) + per-account stack cleanup.
import { useUndo } from "@/lib/undo";
import { useCommandHandler } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { ImpersonationBanner } from "@/features/admin/components/impersonation-banner";
import { Avatar } from "@/components/ui/avatar";
import { OfflineBanner } from "@/components/offline-banner";
import { MobileNavDrawer } from "@/features/layout/components/mobile-nav-drawer";
import { useInteractionFlag } from "@fleet/ui";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@space/shared";
import { Logo } from "@/components/ui/logo";
// F3-C: notification bell (counter + local-first dropdown).
import { NotificationsBell } from "@/features/academy/components/community/notifications-bell";
import { validateAppLayoutSearch } from "@/features/layout/app-search";
import { SESSION_CHECK_TIMEOUT_MS, SESSION_STALE_TIME_MS } from "@/lib/constants";
// Role/capability-based navigation: declarative model + reactive context.
import {
  NAV_SECTIONS,
  type NavItem,
  type NavSection,
  visibleItemsOf,
} from "@/features/navigation/nav-model";
import { NavContextProvider, useNavContext } from "@/features/navigation/use-nav-context";
import { RouteHelpLink } from "@/features/help/components/route-help-link";
import { useKernel } from "@fleet/kernel";

/**
 * Slot a copy of the navigation is mounted into.
 *
 * The shell mounts the SAME navigation TWICE: the desktop sidebar
 * (`hidden md:block`) and the mobile drawer, which since the motion change
 * is **always in the DOM** so its close can be animated (a node that
 * unmounts vanishes in the same frame). Both copies coexist in the DOM at
 * any width: one of them is only hidden by CSS, and `display:none` doesn't
 * take it out of the document.
 *
 * Two nodes with the SAME `data-testid` make Playwright's strict mode
 * refuse to pick one ("resolved to 2 elements") and **everything** that
 * navigates through the sidebar falls over. It's not a test problem: a
 * `data-testid` is an identifier and duplicating it stops it from
 * identifying anything. That's why the drawer's copy suffixes its own,
 * the same convention `NotificationsBell` already had with its `slot`
 * prop (see notifications-bell.tsx).
 */
type NavSlot = "desktop" | "drawer";

const slotTestId = (name: string, slot: NavSlot) => (slot === "drawer" ? `${name}-drawer` : name);

/**
 * The nav's two halves (top scrollable, "Account" anchored at the bottom)
 * are module-level constants, NOT the result of filtering during render.
 *
 * The bar used to iterate `visibleSections(ctx)`, which builds a new
 * object per section on every call. `<For>` reconciles by REFERENCE, so
 * every context re-evaluation —and the session store emits on every
 * navigation, even when nothing changes— handed it "new" sections and
 * threw away the whole subtree: all links unmounted and remounted, which
 * is the menu flicker on navigation. With fixed sections, the only
 * reactive thing is WHICH items of each are visible.
 */
const MAIN_NAV_SECTIONS = NAV_SECTIONS.filter((section) => section.id !== "account");
const ACCOUNT_NAV_SECTIONS = NAV_SECTIONS.filter((section) => section.id === "account");

/** Content equality of an item list (same references, in order). */
const sameNavItems = (a: NavItem[], b: NavItem[]) =>
  a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * SPACE's shell: role/capability-based navigation (declarative nav-model) +
 * Outlet. Local-first and guest-first — content resolves offline; the
 * shell paints instantly while PowerSync initializes. Each item's
 * visibility reacts to an account/org switch (reactive context, i18n with no frozen t()).
 */
function SpaceShell() {
  const [mobileOpen, setMobileOpen] = createSignal(false);
  // With the drawer open, the horizontal gesture is its own (drag to
  // close). Any page gesture listens on the window and would fire
  // underneath, so it's announced in the interaction context instead of
  // letting every gesture re-deduce from the DOM whether there's a drawer in front.
  useInteractionFlag({ "nav-drawer.open": mobileOpen });
  const session = useSession();
  const navigate = useNavigate();
  const { t } = useI18n();
  const navContext = useNavContext();
  const location = useLocation();

  // ── F4-A: global undo (L1) ────────────────────────────────────────────────
  // Ctrl/Cmd+Z undoes the last registered action (in-memory stack). The
  // stack is CLEARED on an account switch: an inverse must never run
  // against another session (UNDO_TRASH_LIGHT's multi-account gotcha).
  const { undoLast, clearUndoStack } = useUndo();
  useCommandHandler(COMMAND_IDS.UNDO, () => {
    void undoLast();
  });
  createEffect(
    on(
      () => session.userId(),
      () => clearUndoStack(),
      { defer: true },
    ),
  );
  // ── end F4-A ──────────────────────────────────────────────────────────────

  // URL-addressable switcher (#15b pattern): `?account=switch` opens it. The
  // overlay itself is mounted ONCE at the layout level (AccountSwitcherHost);
  // this trigger only flips the URL flag.
  const openSwitcher = () => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, account: "switch" }),
      replace: true,
    });
  };

  // Reactive view of the active account for the switcher trigger label. Reading
  // `session.data` registers the dependency so this recomputes when the session
  // changes (switch/sign-in re-primes it).
  const activeAccount = createMemo(() => {
    void session.data;
    return readActiveAccountSync();
  });

  const activeLabel = () => {
    const a = activeAccount();
    return a?.kind === "real" ? a.displayName : t(msg`Guest`);
  };

  const kernelApp = useKernel<AppContribution>();
  const contributedNav = createMemo(() =>
    kernelApp()
      .contributions.filter(
        (c): c is Extract<import("@/composition").AppContribution, { kind: "nav" }> =>
          c.kind === "nav",
      )
      .map((c) => c.item)
      .sort((a, b) => a.order - b.order),
  );

  // A navigation item (identical style/behavior to the original Link).
  const NavLink = (props: { item: NavItem; slot: NavSlot }) => (
    <Link
      to={props.item.to}
      data-testid={slotTestId(props.item.testid, props.slot)}
      onClick={() => setMobileOpen(false)}
      class="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold text-muted-foreground transition-all hover:bg-accent/10 hover:text-accent [&.active]:bg-accent [&.active]:text-accent-foreground [&.active]:shadow-md [&.active]:shadow-accent/25"
      activeProps={{ class: "active" }}
    >
      <span class="transition-transform group-hover:scale-110 group-[.active]:scale-110">
        <Dynamic component={props.item.icon} class="h-5 w-5" />
      </span>
      {t(props.item.label)}
    </Link>
  );

  const SectionHeader = (props: { section: NavSection }) => (
    <Show when={props.section.header}>
      {(header) => (
        <div class="mt-3 px-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">
          {t(header())}
        </div>
      )}
    </Show>
  );

  /**
   * A section: header + its VISIBLE items. The memo compares by content,
   * so as long as the visibility matrix doesn't change `<For>` receives
   * the same array and doesn't touch the DOM; and when it truly changes
   * (login, org switch, flag), the items are still `NAV_SECTIONS`'
   * references and only the affected link enters or leaves. Granular
   * update, no remounts.
   */
  const NavSectionView = (props: { section: NavSection; slot: NavSlot }) => {
    const items = createMemo(() => visibleItemsOf(props.section, navContext()), undefined, {
      equals: sameNavItems,
    });
    return (
      <Show when={items().length > 0}>
        <SectionHeader section={props.section} />
        <For each={items()}>{(item) => <NavLink item={item} slot={props.slot} />}</For>
      </Show>
    );
  };

  const Sidebar = (props: { slot: NavSlot }) => (
    <nav
      class="flex h-full flex-col gap-1.5 p-3"
      data-testid={slotTestId("space-sidebar", props.slot)}
    >
      <div class="mb-3 flex items-center justify-between px-2 py-1">
        <Link
          to="/learn"
          class="flex items-center transition-transform hover:scale-[1.02] active:scale-95"
          onClick={() => setMobileOpen(false)}
        >
          <Logo />
        </Link>
        {/* F3-C: notification bell (already suffixes its testids per slot). */}
        <NotificationsBell slot={props.slot} />
      </div>

      {/* Top sections (scrollable if they overflow). */}
      <div class="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto">
        <For each={MAIN_NAV_SECTIONS}>
          {(section) => <NavSectionView section={section} slot={props.slot} />}
        </For>
        <For each={contributedNav()}>
          {(item) => (
            <Link
              to={item.to}
              onClick={() => setMobileOpen(false)}
              class="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold text-muted-foreground transition-all hover:bg-accent/10 hover:text-accent [&.active]:bg-accent [&.active]:text-accent-foreground [&.active]:shadow-md [&.active]:shadow-accent/25"
              activeProps={{ class: "active" }}
            >
              <span class="transition-transform group-hover:scale-110 group-[.active]:scale-110">
                <Dynamic component={item.icon} class="h-5 w-5" />
              </span>
              {t(item.label)}
            </Link>
          )}
        </For>
      </div>

      {/* Account section — anchored at the bottom, above the switcher. */}
      <For each={ACCOUNT_NAV_SECTIONS}>
        {(section) => <NavSectionView section={section} slot={props.slot} />}
      </For>

      {/* ── Account switcher (multi-account #18) ─────────────────────────────
          Trigger only: flips `?account=switch`; the overlay itself is mounted
          once at the layout level (AccountSwitcherHost, below). */}
      <button
        type="button"
        onClick={() => {
          setMobileOpen(false);
          openSwitcher();
        }}
        class="flex w-full items-center gap-2.5 rounded-2xl border-t border-border/40 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
        aria-label={t(msg`Switch account`)}
        data-testid={slotTestId("account-switcher-trigger", props.slot)}
      >
        <Show
          when={activeAccount()?.kind === "real"}
          fallback={
            <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <UserCircle class="h-4 w-4" />
            </span>
          }
        >
          <Avatar
            userId={activeAccount()?.userId}
            name={activeAccount()?.displayName}
            email={activeAccount()?.email}
            image={activeAccount()?.avatar}
            class="h-7 w-7 text-[10px]"
            testId={slotTestId("account-switcher-avatar", props.slot)}
          />
        </Show>
        <span class="min-w-0 flex-1 text-left">
          <span class="block truncate font-medium text-foreground">{activeLabel()}</span>
          <Show when={activeAccount()?.email}>
            <span class="block truncate text-xs text-muted-foreground/80">
              {activeAccount()?.email}
            </span>
          </Show>
        </span>
        <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-60" />
      </button>
    </nav>
  );

  return (
    <div class="flex h-[100dvh] w-full overflow-hidden bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside class="hidden w-60 shrink-0 border-r border-border bg-surface/40 md:block">
        <Sidebar slot="desktop" />
      </aside>

      {/* Mobile drawer (same mechanism as the canon: always mounted, enters
          and exits with `transform`). Extracted so it can be tested
          without the shell. Its navigation copy suffixes the testids
          (`slot="mobile"`) because it coexists in the DOM with the
          desktop one — see `slotTestId`. */}
      <MobileNavDrawer isOpen={mobileOpen} setOpen={setMobileOpen} closeLabel={t(msg`Close`)}>
        <Sidebar slot="drawer" />
      </MobileNavDrawer>

      <div class="flex min-w-0 flex-1 flex-col">
        {/* Top bar (mobile) */}
        <header class="flex items-center gap-2 border-b border-border p-2 md:hidden">
          <button
            type="button"
            data-testid="open-menu"
            onClick={() => setMobileOpen(true)}
            aria-label={t(msg`Menu`)}
            class="rounded-xl p-1.5 text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent"
          >
            <Menu class="h-6 w-6" />
          </button>
          <Logo />
          {/* F3-C: notification bell (mobile bar). */}
          <div class="ml-auto">
            <NotificationsBell slot="mobile" />
          </div>
        </header>

        {/* F2-C: persistent "Viewing as <name>" bar during impersonation. */}
        <ImpersonationBanner />

        {/* LF-B: global offline banner (airplane mode) — the component
            existed but wasn't mounted in the shell; without it, working
            offline had no honest signal outside the profile pill. */}
        <OfflineBanner />

        <main class="min-h-0 flex-1 overflow-y-auto" data-testid="space-main">
          {/* §M2: screen → guide contextual link. Mounted ONCE and derives
              the active route's area, so no screen is left without help
              by oversight; hidden if a role or flag doesn't enable it. */}
          <RouteHelpLink />
          {/* Per-route entrance: changing the key remounts this wrapper and
              replays `slide-in-up` (transform + opacity only). The key is
              the bare route, untrimmed: the canon strips the detail
              suffix because there that detail is a drawer that leaves the
              page underneath mounted, and without trimming the parent's
              entrance would re-fire when it opens. Doesn't apply here —
              space's `$id` routes are full pages and the only addressable
              overlay goes via a search param (`?account=switch`), which
              doesn't touch the pathname. `prefers-reduced-motion` is
              governed by the global guard. */}
          {/* SCREEN suspense boundary. A read that suspends inside a route
              stops here and only blanks the content area; without this
              boundary it climbs to the router's, which sits above the
              shell and disconnects the whole sidebar from the DOM.

              It isn't the one that fixed today's flicker —that read lived
              in the shell itself (`usePlatformFlags`), above this point,
              which is why no `<Suspense>` from here down could catch it—
              but the one that bounds the damage of the next one: the
              chrome stops depending on no screen ever suspending.
              `fallback={null}` because every screen is local-first and paints its own skeletons. */}
          <Suspense fallback={null}>
            <Show when={location().pathname} keyed>
              <div class="flex min-h-0 flex-1 flex-col animate-slide-in-up will-change-[transform,opacity]">
                <Outlet />
              </div>
            </Show>
          </Suspense>
        </main>
      </div>

      {/* URL-driven account switcher overlay (`?account=switch`) — single mount. */}
      <AccountSwitcherHost />
    </div>
  );
}

/**
 * Root of the `_app` tree: mounts the navigation context ONCE and the
 * shell below it. The provider has to wrap `SpaceShell` (not live inside
 * it) because a component can't consume the context it itself provides —
 * its `useContext` looks upward, not into its own JSX.
 */
function AppShellRoot() {
  return (
    <NavContextProvider>
      <SpaceShell />
    </NavContextProvider>
  );
}

// Deduplicates concurrent beforeLoad calls during the page-load preload burst.
let activeSessionCheck: Promise<void> | null = null;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  // Multi-account: honor an explicit GUEST choice — don't verify with the server.
  if (readActiveAccountSync()?.kind === "guest") {
    return;
  }

  const currentSnapshot = sessionActor.getSnapshot();
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  const fetchSession = async () =>
    api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
  type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

  const sessionResult = await (async () => {
    try {
      // P23/CC-60 · I1-bis: the ceiling comes from `@/lib/constants`, NOT a
      // local literal. Having it here as a loose `const` is what left
      // `_auth`'s sibling guard without the same defense: the app painted
      // `/` and left `/login` blank with the response stalled.
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT_MS);
      });
      return await Promise.race([
        api.GET("/api/auth/get-session", { params: { query: { jwt: true } } }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error or timeout verifying session:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    return;
  }

  const { data, error, response } = sessionResult;

  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({ type: "SERVER_REPLY", session: sessionPayload, error: errorPayload });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      return;
    }
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  validateSearch: validateAppLayoutSearch,
  beforeLoad: () => {
    const accountsState = readAccountsStateSync();
    if (accountsState.accounts.length === 0) {
      void accountsStore.ensureGuestActive();
    } else {
      ensureTabAccount(accountsState);
    }

    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;
      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
  },

  component: AppShellRoot,
});
